import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Environment } from "../../../types";
import type { TokenStats, TokenStatsResponse } from "../types";
import { getApiBase } from "../../../lib/remote-config";

export const TOKEN_STATS_QUERY_KEY = "token-stats";

function buildStatsKey(chainId: number, address: string): string {
  return `${chainId}:${address.toLowerCase()}`;
}

export function useTokenStats(env: Environment, enabled = true) {
  const { data, ...rest } = useQuery<TokenStatsResponse>({
    queryKey: [TOKEN_STATS_QUERY_KEY, env],
    queryFn: async ({ signal }) => {
      const baseUrl = await getApiBase("walletServer", env);
      const res = await fetch(`${baseUrl}/v1/public/token/stats`, {
        cache: "no-store",
        signal,
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch token stats: ${res.status}`);
      }
      const json: TokenStatsResponse = await res.json();
      if (json.code !== 200) {
        throw new Error(`Token stats API error: ${json.code}`);
      }
      return json;
    },
    enabled,
    staleTime: 30_000,
  });

  const statsMap = useMemo(() => {
    const map = new Map<string, TokenStats>();
    if (!data?.data) return map;
    for (const stat of data.data) {
      const key = buildStatsKey(stat.chain_id, stat.address);
      map.set(key, stat);
    }
    return map;
  }, [data]);

  return { statsMap, ...rest };
}

export { buildStatsKey };
